import { showDebugErrToast } from "./components/Toasts";

export type WhenTest = {
  a: { test: string };
  "#name": "when"; // no itx
};

export type WhenDefault = {
  a: { default: unknown };
  "#name": "when"; // no itx
};

export type When = WhenTest | WhenDefault;

export type Choose = {
  a: { ParamRefId: string };
  "#name": "choose"; // no itx
};

export type Child = {
  parent: { items: Child[] };
};

export type ModuleDef = {
  a: { Id: string };
  "#name": "ModuleDef"; // no itx
};

export function isChild(x: unknown): x is Child {
  const y = x as Child;
  return y && "parent" in y; // no itx
}

export function isWhenTest(x: unknown): x is WhenTest {
  const y = x as WhenTest;
  return y && "a" in y && "test" in y.a && y["#name"] == "when"; // no itx
}

export function isWhenDefault(x: unknown): x is WhenDefault {
  const y = x as WhenDefault;
  return y && "a" in y && "default" in y.a && y["#name"] == "when";
}

export function isChoose(x: unknown): x is Choose {
  const y = x as Choose;
  return y && "a" in y && "ParamRefId" in y.a && y["#name"] == "choose"; // no itx
}

export function isModuleDef(x: unknown): x is ModuleDef {
  const y = x as ModuleDef;
  return y && "a" in y && "Id" in y.a && y["#name"] == "ModuleDef"; // no itx
}

export interface Parameter {
  a: { Text: string; ParameterType: string; Acces?: string; Name?: string; Value?: string };
  // Note that the #name parameter is removed, to accept a derefed ParamRef(-Ref) as the corresponding Param,
  // as our implementation of deref does not override the #name property
  // "#name": "Parameter";
}

export function isParameter(x: unknown): x is Parameter {
  const y = x as Parameter;
  return y && "a" in y && "Text" in y.a && "ParameterType" in y.a; // no itx
}

export interface ParameterRef {
  a: { Id: string; RefId: string; Acces?: string; Name?: string; Text?: string; Value?: string };
  // See Parameter
  // "#name": "ParameterRef";
}

export function isParameterRef(x: unknown): x is ParameterRef {
  const y = x as ParameterRef;
  return y && "a" in y && "Id" in y.a && "RefId" in y.a; // no itx
}

export interface ParameterRefRef {
  a: { RefId: string };
  // See Parameter
  // "#name": "ParameterRefRef";
}

export function isParameterRefRef(x: unknown): x is ParameterRefRef {
  const y = x as ParameterRefRef;
  return y && "a" in y && "Text" in y.a && "RefId" in y.a; // no itx
}

export interface Enumeration {
  a: { Value: string; Text: string };
  "#name": "Enumeration"; // no itx
}

export function isEnumeration(x: unknown): x is Enumeration {
  const y = x as Enumeration;
  return y && "a" in y && "Value" in y.a && "Text" in y.a; // no itx
}

export interface TypeRestriction {
  items: Enumeration[];
  "#name": "TypeRestriction"; // no itx
}

export function isTypeRestriction(x: unknown): x is TypeRestriction {
  const y = x as TypeRestriction;
  return (
    y &&
    y["#name"] == "TypeRestriction" &&
    "items" in y && // no itx
    Array.isArray(y.items) &&
    y.items.length > 0 &&
    y.items.every(isEnumeration)
  );
}

export interface TypeNumber {
  a: { maxInclusive: string; minInclusive: string; SizeInBit: string; Type: string };
  "#name": "TypeNumber"; // no itx
}

export function isTypeNumber(x: unknown): x is TypeNumber {
  const y = x as TypeNumber;
  return (
    y &&
    y["#name"] == "TypeNumber" &&
    "a" in y &&
    "maxInclusive" in y.a && // no itx
    "minInclusive" in y.a && // no itx
    "SizeInBit" in y.a && // no itx
    "Type" in y.a && // no itx
    isChildless(y)
  );
}
export interface TypeText {
  a: {
    SizeInBit: string;
  };
  "#name": "TypeText"; // no itx
}

export function isTypeText(x: unknown): x is TypeText {
  const y = x as TypeText;
  return y && y["#name"] == "TypeText" && "a" in y && "SizeInBit" in y.a && isChildless(y); // no itx
}

// TypeNone

export interface TypeNone {
  "#name": "TypeNone"; // no itx
}

export function isTypeNone(x: unknown): x is TypeNone {
  const y = x as TypeNone;
  return y && y["#name"] == "TypeNone" && isChildless(y);
}

// TypePicture
export interface TypePicture {
  "#name": "TypePicture"; // no itx
  a: { RefId: string };
}

export function isTypePicture(x: unknown): x is TypePicture {
  const y = x as TypePicture;
  return y && y["#name"] == "TypePicture" && isChildless(y);
}

// TypeColor
export interface TypeColor {
  "#name": "TypeColor"; // no itx
  a: { Space: string };
}

export function isTypeColor(x: unknown): x is TypeColor {
  const y = x as TypeColor;
  return y && y["#name"] == "TypeColor" && isChildless(y);
}

// TypeFloat

export interface TypeFloat {
  "#name": "TypeFloat"; // no itx
  a: {
    Encoding: string;
    minInclusive: string;
    maxInclusive: string;
  };
}

function isChildless(y: object): boolean {
  return !("items" in y) || !Array.isArray(y.items) || y.items.length == 0; // no itx
}

export function isTypeFloat(x: unknown): x is TypeFloat {
  const y = x as TypeFloat;
  return y && y["#name"] == "TypeFloat" && isChildless(y);
}

export function isTextType(x: any): boolean {
  return x["#name"] == "TypeText";
}

// TypeTime

export interface TypeTime {
  "#name": "TypeTime"; // no itx
}

export function isTypeTime(x: unknown): x is TypeTime {
  const y = x as TypeTime;
  return y && y["#name"] == "TypeTime" && isChildless(y);
}

export type ParameterTypeInner = TypeRestriction | TypeNumber | TypeText | TypeNone;

export interface ParameterType {
  items: [ParameterTypeInner]; // ParameterType always contains exactly one child, therefore we use a Tuple.
  "#name": "ParameterType"; // no itx
  a: { Name: string };
}

export function isParameterType(x: unknown): x is ParameterType {
  const y = x as ParameterType;
  if (
    y &&
    y["#name"] == "ParameterType" &&
    "items" in y && // no itx
    Array.isArray(y.items) &&
    y.items[0] != undefined &&
    y.items.length == 1 &&
    y.a != undefined &&
    !(
      y &&
      y["#name"] == "ParameterType" &&
      "items" in y && // no itx
      Array.isArray(y.items) &&
      y.items[0] != undefined &&
      y.items.length == 1 &&
      y.a != undefined &&
      y.a.Name != undefined
    )
  ) {
    showDebugErrToast("ParameterType without a name encountered");
  }
  return (
    y &&
    y["#name"] == "ParameterType" &&
    "items" in y && // no itx
    Array.isArray(y.items) &&
    y.items[0] != undefined &&
    y.items.length == 1 &&
    y.a != undefined &&
    y.a.Name != undefined
  );
}
